import { format, parseISO } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc, formatInTimeZone } from 'date-fns-tz';
import { enUS } from 'date-fns/locale';

/**
 * Configuración de Timezone para Buenos Aires, Argentina
 * Todas las fechas se manejan en UTC en el backend y se convierten
 * a hora de Buenos Aires para mostrar al usuario
 */

// Timezone oficial de Buenos Aires
export const BUENOS_AIRES_TZ = 'America/Argentina/Buenos_Aires';

// Configuración global de la aplicación
export const APP_CONFIG = {
  timezone: BUENOS_AIRES_TZ,
  locale: enUS, // English for UI
  defaultDateFormat: 'dd/MM/yyyy',
  defaultTimeFormat: 'HH:mm',
  defaultDateTimeFormat: 'dd/MM/yyyy HH:mm',
  defaultMonthDayFormat: 'dd/MM',
  fullDateTimeFormat: "MMMM dd, yyyy 'at' HH:mm",
};

/**
 * Convierte una fecha UTC a hora de Buenos Aires
 * @param {Date|string} date - Fecha en UTC
 * @returns {Date} Fecha en timezone de Buenos Aires
 */
export const toArgentinaTime = (date) => {
  if (!date) return null;
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return utcToZonedTime(dateObj, BUENOS_AIRES_TZ);
};

/**
 * Convierte una fecha de Buenos Aires a UTC
 * @param {Date} date - Fecha en timezone de Buenos Aires
 * @returns {Date} Fecha en UTC
 */
export const fromArgentinaTime = (date) => {
  if (!date) return null;
  return zonedTimeToUtc(date, BUENOS_AIRES_TZ);
};

/**
 * Formats a UTC date to display in Buenos Aires time
 * @param {Date|string} date - Date in UTC
 * @param {string} formatStr - Desired format (optional)
 * @returns {string} Formatted date in Buenos Aires time
 */
export const formatInArgentina = (date, formatStr = APP_CONFIG.defaultDateTimeFormat) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatInTimeZone(dateObj, BUENOS_AIRES_TZ, formatStr, { locale: enUS });
};

/**
 * Formatea solo la fecha (sin hora) en formato argentino
 * @param {Date|string} date - Fecha
 * @returns {string} Fecha formateada (dd/MM/yyyy)
 */
export const formatDateArgentina = (date) => {
  return formatInArgentina(date, APP_CONFIG.defaultDateFormat);
};

/**
 * Formatea solo la hora en formato argentino
 * @param {Date|string} date - Fecha
 * @returns {string} Hora formateada (HH:mm)
 */
export const formatTimeArgentina = (date) => {
  return formatInArgentina(date, APP_CONFIG.defaultTimeFormat);
};

/**
 * Formatea fecha completa con nombre del mes en español
 * @param {Date|string} date - Fecha
 * @returns {string} Fecha formateada (ej: "08 de agosto de 2025 a las 15:30")
 */
export const formatFullDateArgentina = (date) => {
  return formatInArgentina(date, APP_CONFIG.fullDateTimeFormat);
};

/**
 * Obtiene la fecha/hora actual en Buenos Aires
 * @returns {Date} Fecha actual en timezone de Buenos Aires
 */
export const nowInArgentina = () => {
  return utcToZonedTime(new Date(), BUENOS_AIRES_TZ);
};

/**
 * Formatea una fecha relativa (ej: "hace 2 horas", "ayer")
 * @param {Date|string} date - Fecha
 * @returns {string} Fecha relativa
 */
export const formatRelativeArgentina = (date) => {
  if (!date) return '';
  
  const now = nowInArgentina();
  const targetDate = toArgentinaTime(date);
  
  const diffMs = now - targetDate;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  
  return formatDateArgentina(date);
};

/**
 * Agrupa mensajes por día en timezone de Buenos Aires
 * @param {Array} messages - Array de mensajes con timestamps
 * @returns {Object} Mensajes agrupados por día
 */
export const groupMessagesByDay = (messages) => {
  const groups = {};
  
  messages.forEach(message => {
    const dayKey = formatDateArgentina(message.message_timestamp);
    if (!groups[dayKey]) {
      groups[dayKey] = [];
    }
    groups[dayKey].push(message);
  });
  
  return groups;
};

/**
 * Determina si una fecha es hoy en Buenos Aires
 * @param {Date|string} date - Fecha a verificar
 * @returns {boolean} True si es hoy
 */
export const isToday = (date) => {
  const today = formatDateArgentina(nowInArgentina());
  const dateToCheck = formatDateArgentina(date);
  return today === dateToCheck;
};

/**
 * Determina si una fecha fue ayer en Buenos Aires
 * @param {Date|string} date - Fecha a verificar
 * @returns {boolean} True si fue ayer
 */
export const isYesterday = (date) => {
  const now = nowInArgentina();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const yesterdayStr = formatDateArgentina(yesterday);
  const dateToCheck = formatDateArgentina(date);
  return yesterdayStr === dateToCheck;
};

/**
 * Obtiene un label amigable para la fecha
 * @param {Date|string} date - Fecha
 * @returns {string} "Today", "Yesterday" or the formatted date
 */
export const getDateLabel = (date) => {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return formatDateArgentina(date);
};

/**
 * Formats a timestamp like WhatsApp
 * @param {Date|string} date - The date to format
 * @returns {string} Today: "HH:mm", Yesterday: "Yesterday", Older: "dd/MM/yyyy"
 */
export const formatWhatsappTimestamp = (date) => {
  if (!date) return '';

  if (isToday(date)) {
    return formatTimeArgentina(date);
  }
  if (isYesterday(date)) {
    return 'Yesterday';
  }
  
  // For older dates, you might want the day of the week or the full date
  // For simplicity here, we'll return the formatted date.
  return formatDateArgentina(date);
};

/**
 * Formats a duration given in minutes into a human-readable string.
 * e.g., 90.5 -> "1h 30m", 55.2 -> "55m 12s", 0.5 -> "30s"
 * @param {number} minutes - The duration in minutes.
 * @returns {string} The formatted duration string.
 */
export function formatDuration(minutes) {
  if (typeof minutes !== 'number' || isNaN(minutes)) {
    return '0s';
  }

  const totalSeconds = Math.round(minutes * 60);
  
  const hours = Math.floor(totalSeconds / 3600);
  const remainingSeconds = totalSeconds % 3600;
  const mins = Math.floor(remainingSeconds / 60);
  const secs = remainingSeconds % 60;

  let result = '';
  if (hours > 0) {
    result += `${hours}h `;
  }
  if (mins > 0) {
    result += `${mins}m `;
  }
  // Always show seconds if the total duration is less than a minute, or if there are remaining seconds
  if (secs > 0 || result === '') {
    result += `${secs}s`;
  }

  return result.trim();
}
